import {useEffect, useState} from 'react';
import {useTheme} from '@mui/material';
import {translations} from 'presentation/translations-new';
import {FavoriteButtonProps} from '../types';
import {usePostFavoriteDoctorMutation} from '../mutations/usePostFavoriteDoctorMutation';
import {useDeleteFavoriteDoctorMutation} from '../mutations/useDeleteFavoriteDoctorMutation';
import {useFavoriteDoctorUpdatedStore} from '../store';

const {favoriteButton} = translations['pt-br'].privateFlow.guideFlow.favoriteDoctors;

export function useFavoriteDoctorsLikeButton({
	selected,
	cdPrestador,
	cdEspecialidade,
	cdSubEspecialidade,
}: FavoriteButtonProps) {
	const favoriteDoctorUpdated = useFavoriteDoctorUpdatedStore(
		state => state.favoriteDoctorUpdated,
	);
	const setFavoriteDoctorUpdated = useFavoriteDoctorUpdatedStore(
		state => state.setFavoriteDoctorUpdated,
	);
	const {colors} = useTheme();
	const [isSelected, setIsSelected] = useState(selected);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackBarMessage, setSnackbarMessage] = useState('');

	useEffect(() => {
		setIsSelected(selected);
	}, [selected]);

	const {
		mutateAsync: favoriteDoctorMutation,
		isPending: isLoadingFavorite,
		isSuccess: isFavoriteSuccess,
		isError: isFavoriteError,
		reset: resetFavorite,
	} = usePostFavoriteDoctorMutation();

	const {
		mutateAsync: unfavoriteDoctorMutation,
		isPending: isLoadingUnfavorite,
		isSuccess: isUnfavoriteSuccess,
		isError: isUnfavoriteError,
		reset: resetUnfavorite,
	} = useDeleteFavoriteDoctorMutation();

	const handleToggleFavorite = async () => {
		if (isSelected) {
			resetFavorite();
			await unfavoriteDoctorMutation({cdPrestador, cdEspecialidade, cdSubEspecialidade});
		} else {
			resetUnfavorite();
			await favoriteDoctorMutation({cdPrestador, cdEspecialidade, cdSubEspecialidade});
		}
	};

	const isLoading = isLoadingFavorite || isLoadingUnfavorite;

	useEffect(() => {
		if (!isFavoriteSuccess) {
			return;
		}

		setIsSelected(true);
		setSnackbarMessage(favoriteButton.add);
		setOpenSnackbar(true);
		setFavoriteDoctorUpdated({
			cdPrestador,
			cdEspecialidade,
			cdSubEspecialidade,
			selected: true,
		});
	}, [
		isFavoriteSuccess,
		cdPrestador,
		cdEspecialidade,
		cdSubEspecialidade,
		setFavoriteDoctorUpdated,
	]);

	useEffect(() => {
		if (!isUnfavoriteSuccess) {
			return;
		}

		setIsSelected(false);
		setSnackbarMessage(favoriteButton.remove);
		setOpenSnackbar(true);
		setFavoriteDoctorUpdated({
			cdPrestador,
			cdEspecialidade,
			cdSubEspecialidade,
			selected: false,
		});
	}, [
		isUnfavoriteSuccess,
		cdPrestador,
		cdEspecialidade,
		cdSubEspecialidade,
		setFavoriteDoctorUpdated,
	]);

	useEffect(() => {
		if (!isFavoriteError && !isUnfavoriteError) {
			return;
		}

		setSnackbarMessage(isFavoriteError ? favoriteButton.addError : favoriteButton.removeError);
		setOpenSnackbar(true);
	}, [isFavoriteError, isUnfavoriteError]);

	useEffect(() => {
		if (
			favoriteDoctorUpdated &&
			favoriteDoctorUpdated.cdPrestador === cdPrestador &&
			favoriteDoctorUpdated.cdEspecialidade === cdEspecialidade &&
			favoriteDoctorUpdated.cdSubEspecialidade === cdSubEspecialidade
		) {
			setIsSelected(favoriteDoctorUpdated.selected);
		}
		return () => {
			const current = useFavoriteDoctorUpdatedStore.getState().favoriteDoctorUpdated;
			if (
				current?.cdPrestador === cdPrestador &&
				current?.cdEspecialidade === cdEspecialidade &&
				current?.cdSubEspecialidade === cdSubEspecialidade
			) {
				setFavoriteDoctorUpdated(null);
			}
		};
	}, [
		favoriteDoctorUpdated,
		cdPrestador,
		cdEspecialidade,
		cdSubEspecialidade,
		setFavoriteDoctorUpdated,
	]);

	return {
		isSelected,
		openSnackbar,
		snackBarMessage,
		isLoading,
		colors,
		handleToggleFavorite,
		setOpenSnackbar,
		setSnackbarMessage,
	};
}
